import React, { useEffect, useState } from 'react';
import {
  Box,
  Stack,
  Button,
  Avatar,
  TextField,
  Typography,
  Container,
  IconButton,
  Chip,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Badge,
  Fade,
  Grid,
  CircularProgress,
} from '@mui/material';
import {
  UtensilsCrossed,
  Play,
  FileText,
  Send,
  Reply,
  Upload,
  Video,
  X,
  Flame,
  Beef,
  Droplets,
  Zap,
  Trash2,
  Import,
} from 'lucide-react';
import { useAuthContext } from 'src/auth/hooks';
import { Lightbox, useLightBox } from 'src/components/lightbox';
import {
  add_library_food_comment,
  add_library_food_comment_media,
  get_library_food_comments,
  delete_library_food_comment,
} from 'src/auth/context/jwt/library';
import { toast } from 'sonner';
import { useLocation, useNavigate } from 'react-router';
import { LoadingButton } from '@mui/lab';
import { ChatThreadSkeleton } from './chat-skeleton';
import { paths } from 'src/routes/paths';

// Nutrition icon map
const NUTRITION_ICONS = {
  Calories: <Flame size={14} />,
  Protein: <Beef size={14} />,
  Fats: <Droplets size={14} />,
  Water: <Droplets size={14} />,
};

// Food Item Details Card
function FoodItemDetailsCard({ foodItem }) {
  const [showMedia, setShowMedia] = useState(true);

  const mediaFromComments =
    foodItem?.comments
      ?.filter((c) => c.media && c.media.length > 0)
      .flatMap((c) =>
        c.media.map((m) => ({
          ...m,
          commentAuthor: c.user?.name,
          commentDate: c.created_at,
          commentMessage: c.message,
        }))
      ) || [];

  const videos = mediaFromComments.filter((i) => i.type === 'video');
  const images = mediaFromComments.filter((i) => i.type === 'image');

  const videoSlides = videos.map((v) => ({
    type: 'video',
    width: 1280,
    height: 720,
    sources: [{ src: v.url, type: 'video/mp4' }],
    title: `Video by ${v.commentAuthor}`,
    description: v.commentMessage,
  }));

  const imageSlides = images.map((img) => ({
    type: 'image',
    sources: [{ src: img.url }],
    src: img.url,
    title: `Image by ${img.commentAuthor}`,
    description: img.commentMessage,
  }));

  const videoLightbox = useLightBox(videoSlides);
  const imageLightbox = useLightBox(imageSlides);

  return (
    <>
      <Card
        elevation={0}
        sx={{ mb: 4, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 3, overflow: 'hidden' }}
      >
        {/* Header */}
        <Box sx={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', color: 'white', p: 4 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <UtensilsCrossed size={32} />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {foodItem?.name} ( {foodItem?.quantity} {foodItem?.unit} )
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85, textTransform: 'uppercase' }}>
                {foodItem?.path}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            {foodItem?.nutritions?.map((n) => (
              <Chip
                key={n.id}
                label={`${n.name}: ${n.quantity} ${n.unit}`}
                sx={{ borderColor: 'rgba(255,255,255,0.3)', color: 'white' }}
                variant="outlined"
              />
            ))}
          </Stack>
        </Box>

        {/* Media Section */}
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            {videos.length === 0 && images.length === 0 ? (
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                No Videos / Images
              </Typography>
            ) : (
              <>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Videos & Images
                </Typography>
                <Button
                  size="small"
                  onClick={() => setShowMedia(!showMedia)}
                  sx={{ color: '#667eea' }}
                >
                  {showMedia ? 'Hide' : 'Show'}
                </Button>
              </>
            )}
          </Stack>

          {showMedia && (
            <>
              {videos.length > 0 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Videos ({videos.length})
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      overflowX: 'auto',
                      pb: 2,
                      '&::-webkit-scrollbar': { height: 8 },
                      '&::-webkit-scrollbar-thumb': { backgroundColor: '#667eea', borderRadius: 4 },
                    }}
                  >
                    {videos.map((video, index) => (
                      <Card
                        key={index}
                        onClick={() => videoLightbox.onOpen(video.url)}
                        sx={{
                          minWidth: 200,
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            position: 'relative',
                            width: 200,
                            height: 150,
                            bgcolor: '#000',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <IconButton
                            sx={{
                              bgcolor: 'rgba(102, 126, 234, 0.9)',
                              color: 'white',
                              '&:hover': { bgcolor: 'rgba(102, 126, 234, 1)' },
                            }}
                          >
                            <Play size={32} />
                          </IconButton>
                        </Box>
                        <CardContent sx={{ p: 1.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            By {video.commentAuthor}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </>
              )}

              {images.length > 0 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Images ({images.length})
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      overflowX: 'auto',
                      pb: 2,
                      '&::-webkit-scrollbar': { height: 8 },
                      '&::-webkit-scrollbar-thumb': { backgroundColor: '#667eea', borderRadius: 4 },
                    }}
                  >
                    {images.map((image, index) => (
                      <Card
                        key={index}
                        onClick={() => imageLightbox.onOpen(image.url)}
                        sx={{
                          minWidth: 200,
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                          },
                        }}
                      >
                        <CardMedia
                          component="img"
                          height="150"
                          image={image.url}
                          alt={`Image by ${image.commentAuthor}`}
                          sx={{ objectFit: 'cover' }}
                        />
                        <CardContent sx={{ p: 1.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            By {image.commentAuthor}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Lightbox
        index={videoLightbox.selected}
        slides={videoSlides}
        open={videoLightbox.open}
        close={videoLightbox.onClose}
        disableSlideshow
        disableZoom
      />
      <Lightbox
        index={imageLightbox.selected}
        slides={imageSlides}
        open={imageLightbox.open}
        close={imageLightbox.onClose}
        disableSlideshow
      />
    </>
  );
}

// Media Preview Component
function FoodMediaPreview({ file, onRemove }) {
  const isVideo = file?.type?.includes('video');

  return (
    <Box
      sx={{
        position: 'relative',
        width: 120,
        height: 120,
        borderRadius: 2,
        overflow: 'hidden',
        border: '2px solid #667eea',
      }}
    >
      {isVideo ? (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            bgcolor: '#000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Video size={40} color="white" />
        </Box>
      ) : (
        <Box
          component="img"
          src={file.preview || file.media}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
      <IconButton
        size="small"
        onClick={onRemove}
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          bgcolor: 'rgba(0,0,0,0.6)',
          color: 'white',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
        }}
      >
        <X size={16} />
      </IconButton>
    </Box>
  );
}

// Food Comment Form
function FoodCommentForm({ onSubmit, loading, disable }) {
  const { user } = useAuthContext();
  const [comment, setComment] = useState('');
  const [media, setMedia] = useState([]);

  const uploadMedia = async (file, index) => {
    try {
      const res = await add_library_food_comment_media({
        file,
        type: file?.type?.includes('video') ? 'video' : 'image',
      });
      setMedia((prev) =>
        prev.map((m, i) =>
          i === index ? { ...m, uploading: false, id: res?.data?.id, media: res?.data?.media } : m
        )
      );
    } catch (error) {
      toast.error(typeof error === 'string' ? error : error?.error);
      setMedia((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    const placeholders = files.map((file) => ({ file, type: file.type, uploading: true }));
    setMedia((prev) => [...prev, ...placeholders]);
    for (let i = 0; i < files.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await uploadMedia(files[i], media.length + i);
    }
    e.target.value = null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (comment.trim() || media.length > 0) {
      const mediaIdsString = media
        .map((m) => m.id)
        .filter(Boolean)
        .join(',');
      onSubmit({ comment, comment_media_ids: mediaIdsString });
      setComment('');
      setMedia([]);
    }
  };

  return (
    <Card elevation={0} sx={{ mb: 4, border: '2px solid rgba(102,126,234,0.15)', borderRadius: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Stack spacing={2} component="form" onSubmit={handleSubmit}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#667eea', color: 'white' }}>
              <FileText size={20} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {user?.role?.id === 4 ? 'Add a comment' : 'Share Your Feedback / Add Comment'}
            </Typography>
          </Stack>

          <TextField
            fullWidth
            multiline
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Share thoughts, ask about nutrition, or upload images/videos..."
            variant="outlined"
          />

          {media.length > 0 && (
            <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
              {media.map((item, idx) => (
                <Box key={idx}>
                  {item.uploading ? (
                    <Box
                      sx={{
                        width: 120,
                        height: 120,
                        borderRadius: 2,
                        bgcolor: 'rgba(0,0,0,0.1)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        border: '2px solid #667eea',
                      }}
                    >
                      <CircularProgress size={32} sx={{ color: '#667eea' }} />
                    </Box>
                  ) : (
                    <FoodMediaPreview
                      file={item}
                      onRemove={() => setMedia(media.filter((_, i) => i !== idx))}
                    />
                  )}
                </Box>
              ))}
            </Stack>
          )}

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Button
              disabled={disable}
              component="label"
              startIcon={<Upload size={18} />}
              sx={{ color: '#667eea' }}
            >
              Upload Media (Multiple)
              <input
                type="file"
                hidden
                multiple
                accept="image/*,video/*"
                onChange={handleFileSelect}
              />
            </Button>

            <LoadingButton
              loading={loading}
              type="submit"
              variant="contained"
              disabled={
                (!comment.trim() && media.length === 0) || media.some((m) => m.uploading) || disable
              }
              endIcon={<Send size={18} />}
              sx={{
                bgcolor: '#667eea',
                '&:hover': { bgcolor: '#5568d3' },
                borderRadius: 2,
                px: 3,
              }}
            >
              Post Comment
            </LoadingButton>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

// Food Reply Form
function FoodReplyForm({ loading, parent, onSubmit, onCancel, replyTo, disable }) {
  const [reply, setReply] = useState('');
  const [media, setMedia] = useState([]);

  const uploadMedia = async (file, index) => {
    try {
      const res = await add_library_food_comment_media({
        file,
        type: file?.type?.includes('video') ? 'video' : 'image',
      });
      setMedia((prev) =>
        prev.map((m, i) =>
          i === index ? { ...m, uploading: false, id: res?.data?.id, media: res?.data?.media } : m
        )
      );
    } catch (error) {
      toast.error(typeof error === 'string' ? error : error?.error);
      setMedia((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    const placeholders = files.map((file) => ({ file, type: file.type, uploading: true }));
    setMedia((prev) => [...prev, ...placeholders]);
    for (let i = 0; i < files.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await uploadMedia(files[i], media.length + i);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (reply.trim() || media.length > 0) {
      const mediaIdsString = media
        .map((m) => m.id)
        .filter(Boolean)
        .join(',');
      onSubmit({ parent_id: parent?.id, comment: reply, comment_media_ids: mediaIdsString });
      setReply('');
      setMedia([]);
    }
  };

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        bgcolor: 'background.neutral',
        borderRadius: 2,
        border: '1px solid rgba(102,126,234,0.15)',
      }}
    >
      <Stack spacing={2} component="form" onSubmit={handleSubmit}>
        <Typography variant="caption" color="text.secondary">
          Replying to <strong>@{replyTo}</strong>
        </Typography>

        <TextField
          fullWidth
          multiline
          rows={2}
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder="Write your reply..."
          variant="outlined"
          size="small"
        />

        {media.map((item, idx) => (
          <Box key={idx}>
            {item.uploading ? (
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: 2,
                  bgcolor: 'rgba(0,0,0,0.1)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: '2px solid #667eea',
                }}
              >
                <CircularProgress size={32} sx={{ color: '#667eea' }} />
              </Box>
            ) : (
              <FoodMediaPreview
                file={item}
                onRemove={() => setMedia(media.filter((_, i) => i !== idx))}
              />
            )}
          </Box>
        ))}

        <Stack direction="row" justifyContent="space-between">
          <Button
            component="label"
            size="small"
            startIcon={<Upload size={16} />}
            sx={{ color: '#667eea' }}
            disabled={disable}
          >
            Add Media (Multiple)
            <input
              type="file"
              hidden
              multiple
              accept="image/*,video/*"
              onChange={handleFileSelect}
            />
          </Button>

          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={onCancel} variant="outlined">
              Cancel
            </Button>
            <LoadingButton
              loading={loading}
              type="submit"
              size="small"
              variant="contained"
              disabled={(!reply.trim() && media.length === 0) || disable}
              sx={{ bgcolor: '#667eea', '&:hover': { bgcolor: '#5568d3' } }}
            >
              Reply
            </LoadingButton>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}

// Food Comment Media Display
function FoodCommentMedia({ media, commentAuthor, commentMessage }) {
  if (!media || media.length === 0) return null;

  const mediaSlides = media.map((item) =>
    item.type === 'video'
      ? {
          type: 'video',
          width: 1280,
          height: 720,
          sources: [{ src: item.url, type: 'video/mp4' }],
          title: `Video by ${commentAuthor}`,
          description: commentMessage,
        }
      : {
          type: 'image',
          sources: [{ src: item.url }],
          src: item.url,
          title: `Image by ${commentAuthor}`,
          description: commentMessage,
        }
  );

  const mediaLightbox = useLightBox(mediaSlides);

  return (
    <>
      <Grid container spacing={1} sx={{ mt: 1 }}>
        {media.map((item, idx) => (
          <Grid item xs={6} sm={4} md={3} key={idx}>
            <Card
              elevation={0}
              onClick={() => mediaLightbox.onOpen(item.url)}
              sx={{
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: 2,
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  opacity: 0.9,
                  transform: 'scale(1.05)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
              }}
            >
              {item.type === 'video' ? (
                <Box sx={{ position: 'relative', paddingTop: '100%', bgcolor: '#000' }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <IconButton
                      sx={{
                        bgcolor: 'rgba(102, 126, 234, 0.9)',
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(102, 126, 234, 1)' },
                      }}
                    >
                      <Play size={32} />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <CardMedia
                  component="img"
                  image={item.url}
                  alt={`Upload ${idx + 1}`}
                  sx={{ aspectRatio: '1/1', objectFit: 'cover' }}
                />
              )}
            </Card>
          </Grid>
        ))}
      </Grid>
      <Lightbox
        index={mediaLightbox.selected}
        slides={mediaSlides}
        open={mediaLightbox.open}
        close={mediaLightbox.onClose}
        disableSlideshow
      />
    </>
  );
}

// Food Comment Item
function FoodCommentItem({
  comment,
  onReply,
  index,
  reply_commenting,
  setreply_commenting,
  disable,
  onRefresh,
  screen,
}) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingReplyId, setDeletingReplyId] = useState(null);

  const handleDeleteComment = async (id, isReply = false, replyId = null) => {
    try {
      if (isReply) {
        setDeletingReplyId(replyId);
      } else {
        setDeleting(true);
      }
      const res = await delete_library_food_comment({ id: isReply ? replyId : id });
      toast.success(res?.message);
      onRefresh && onRefresh();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : error?.error);
    } finally {
      if (isReply) {
        setDeletingReplyId(null);
      } else {
        setDeleting(false);
      }
    }
  };

  return (
    <Fade in timeout={300 + index * 100}>
      <Card
        elevation={0}
        sx={{
          mb: 2,
          border: '1px solid rgba(0,0,0,0.08)',
          borderRadius: 2,
          transition: 'all 0.3s ease',
          '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.08)', transform: 'translateY(-2px)' },
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: '#10b981',
                      border: '2px solid white',
                    }}
                  />
                }
              >
                <Avatar
                  alt={comment?.user?.name}
                  src={comment?.user?.avatar}
                  sx={{
                    width: { xs: 40, sm: 48 },
                    height: { xs: 40, sm: 48 },
                    bgcolor: '#667eea',
                    fontWeight: 600,
                  }}
                >
                  {comment?.user?.name?.charAt(0).toUpperCase()}
                </Avatar>
              </Badge>

              <Box sx={{ flexGrow: 1 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
                  spacing={{ xs: 1, sm: 0 }}
                >
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, fontSize: { xs: '0.9rem', sm: '1rem' } }}
                    >
                      {comment?.user?.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    >
                      {new Date(comment?.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={0.5}>
                    <IconButton
                      size="small"
                      onClick={() => setShowReplyForm(!showReplyForm)}
                      sx={{ color: showReplyForm ? '#667eea' : 'text.secondary' }}
                    >
                      <Reply size={18} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteComment(comment?.id)}
                      disabled={deleting}
                      sx={{ color: 'error.main' }}
                    >
                      {deleting ? (
                        <CircularProgress size={16} color="error" />
                      ) : (
                        <Trash2 size={18} />
                      )}
                    </IconButton>
                  </Stack>
                </Stack>

                <Typography
                  variant="body1"
                  sx={{ mt: 1.5, lineHeight: 1.7, fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {comment?.message}
                </Typography>

                <FoodCommentMedia
                  media={comment.media}
                  commentAuthor={comment?.user?.name}
                  commentMessage={comment.message}
                />

                {comment.replies && comment.replies.length > 0 && (
                  <Box
                    sx={{
                      mt: 3,
                      ml: { xs: 1, sm: 2 },
                      pl: { xs: 2, sm: 3 },
                      borderLeft: '2px solid #e2e8f0',
                    }}
                  >
                    {comment.replies.map((reply) => (
                      <Box key={reply?.id} sx={{ mb: 2 }}>
                        <Stack direction="row" spacing={2}>
                          <Avatar
                            src={reply?.user?.avatar}
                            sx={{ width: 32, height: 32, bgcolor: '#667eea' }}
                          >
                            {reply.user?.name?.charAt(0)}
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              alignItems={{ xs: 'flex-start', sm: 'center' }}
                              spacing={1}
                              justifyContent="space-between"
                            >
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                  }}
                                >
                                  {reply?.user?.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}
                                >
                                  {new Date(reply?.created_at).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                  })}
                                </Typography>
                              </Stack>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteComment(comment?.id, true, reply?.id)}
                                disabled={deletingReplyId === reply?.id}
                                sx={{ color: 'error.main' }}
                              >
                                {deletingReplyId === reply?.id ? (
                                  <CircularProgress size={14} color="error" />
                                ) : (
                                  <Trash2 size={16} />
                                )}
                              </IconButton>
                            </Stack>
                            <Typography
                              variant="body2"
                              sx={{ mt: 0.5, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                            >
                              {reply?.message}
                            </Typography>
                            <FoodCommentMedia
                              media={reply?.media}
                              commentAuthor={reply?.user?.name}
                              commentMessage={reply.message}
                            />
                          </Box>
                        </Stack>
                      </Box>
                    ))}
                  </Box>
                )}

                {showReplyForm && (
                  <FoodReplyForm
                    disable={disable}
                    loading={reply_commenting}
                    parent={comment}
                    replyTo={comment.user?.name}
                    onSubmit={(data) => {
                      setreply_commenting(true);
                      onReply(data);
                    }}
                    onCancel={() => setShowReplyForm(false)}
                  />
                )}
              </Box>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Fade>
  );
}

// Main Component
export default function FoodItemDiscussion() {
  const location = useLocation();
  const navigate = useNavigate();
  const { food_item } = location.state || {};

  const [comments, setComments] = useState([]);
  const [get_loading, setget_loading] = useState(false);
  const [commenting, setcommenting] = useState(false);
  const [reply_commenting, setreply_commenting] = useState(false);

  const foodItemWithComments = { ...food_item, comments };

  const fetchComments = async () => {
    try {
      const res = await get_library_food_comments({ food_item_id: food_item?.id });
      setComments(res?.data?.data || []);
    } catch (error) {
      console.log(error);
    } finally {
      setget_loading(false);
    }
  };

  useEffect(() => {
    setget_loading(true);
    fetchComments();
    // eslint-disable-next-line
  }, [food_item?.id]);

  const handleAddComment = async (data) => {
    setcommenting(true);
    try {
      await add_library_food_comment({ ...data, food_item_id: food_item?.id });
      await fetchComments();
    } catch (error) {
      console.log(error);
    } finally {
      setcommenting(false);
      setreply_commenting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      {!get_loading && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<Import size={18} />}
            onClick={() =>
              navigate(paths.dashboard.general.LogNotesHistory, {
                state: {
                  currentWorkout: food_item?.name,
                  entity_id: food_item?.id,
                  screen: 'food_items',
                },
              })
            }
            sx={{
              borderColor: '#667eea',
              color: '#667eea',
              '&:hover': {
                borderColor: '#5568d3',
                backgroundColor: 'rgba(102, 126, 234, 0.04)',
              },
              borderRadius: 2,
              px: 3,
            }}
          >
            Notes History
          </Button>
        </Stack>
      )}

      <FoodCommentForm onSubmit={handleAddComment} loading={commenting} disable={false} />

      {get_loading ? (
        <ChatThreadSkeleton />
      ) : (
        <>
          <FoodItemDetailsCard foodItem={foodItemWithComments} />

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Discussion ({comments.length} comments)
            </Typography>
            <Divider />
          </Box>

          <Stack spacing={0}>
            {comments.map((comment, index) => (
              <FoodCommentItem
                key={comment.id}
                comment={comment}
                onReply={handleAddComment}
                index={index}
                reply_commenting={reply_commenting}
                setreply_commenting={setreply_commenting}
                disable={false}
                onRefresh={fetchComments}
              />
            ))}
          </Stack>
        </>
      )}
    </Container>
  );
}
